/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { Maybe } from '@codegrade/maybe';
import { NonEmptyList } from '@codegrade/non-empty-list';
import { AssertionError } from './assert';
import { DefaultMap } from './defaultdict';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function* islice(iter, start, end) {
    let i = 0;
    for (const el of iter) {
        if (end != null && i >= end) {
            break;
        }
        i++;
        if (i < start) {
            continue;
        }
        yield el;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function* chain(iterables) {
    for (const iterable of iterables) {
        yield* iterable;
    }
}
/**
 * A wrapper around `chain` to be used easily with arrays.
 *
 * @param arrays The arrays you want to join into a single iterable.
 * @returns The chained arrays.
 */
export function chainArrays(...arrays) {
    return chain(arrays);
}
/**
 * The same as {@link Array.some} but for iterables.
 *
 * @param iter The iterable to check.
 * @param cond The condition to check with.
 * @returns If `cond` returned `true` at least one time.
 */
export function some(iter, cond) {
    let i = 0;
    for (const el of iter) {
        if (cond(el, i)) {
            return true;
        }
        i += 1;
    }
    return false;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function all(iter, cond) {
    return !some(iter, (el, idx) => !cond(el, idx));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function* map(arr, mapper) {
    let i = 0;
    for (const el of arr) {
        yield mapper(el, i);
        i++;
    }
}
/**
 * Map function that returns an iterable over a iterable returning a
 * non nested iterable.
 *
 * Only use this function if it makes sense to get an iterable,
 * otherwise just use flatMap1 from this module.
 *
 * @param arr The iterable to map.
 * @param mapper The mapper that will be applied for each element in
 * arr.
 * @yields An iterable containing each element yielded by mapper.
 */
export function* flatMap1Iter(arr, mapper) {
    let i = 0;
    for (const el of arr) {
        yield* mapper(el, i);
        i++;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function flatMap1(arr, mapper) {
    const res = [];
    let i = 0;
    for (const el of arr) {
        for (const subEl of mapper(el, i)) {
            res.push(subEl);
        }
        i++;
    }
    return res;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function* flat1(arr) {
    for (const subArr of arr) {
        for (const el of subArr) {
            yield el;
        }
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function reduce(it, reducer, initial) {
    let index = 0;
    let res = initial;
    for (const el of it) {
        res = reducer(res, el, index);
        index += 1;
    }
    return res;
}
/** @inheritdoc */
export function find(it, pred) {
    for (const el of it) {
        if (pred(el)) {
            return Maybe.of(el);
        }
    }
    return Maybe.empty();
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function first(it) {
    // eslint-disable-next-line no-unreachable-loop
    for (const el of it) {
        return Maybe.of(el);
    }
    return Maybe.empty();
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function groupBy(arr, mapper) {
    return reduce(arr, (acc, el, index) => {
        const key = mapper(el, index);
        acc.get(key).push(el);
        return acc;
    }, new DefaultMap((_) => []));
}
/**
 * Create a new iterable by applying a function to each element and yielding the unwrapped value if
 * the result is a `Just`.
 *
 * This functions acts as a combination of `map` and `filter`, similar to `filter_map` in some
 * languages.
 *
 * If you just want to filter on a boolean condition without narrowing the type, using `filterBool`
 * is slightly more efficient.
 *
 * @param filterFunc A function that takes an element and its index, and returns a `Maybe<Y>`. If
 * the result is `Just`, the unwrapped value is yielded.
 * @param arr The input iterable.
 * @yields The values from the input iterable for which `filterFunc` returned a `Just`, unwrapped
 * from the `Maybe`.
 */
export function* filter(filterFunc, arr) {
    let i = 0;
    for (const el of arr) {
        const val = filterFunc(el, i++);
        if (val.isJust()) {
            yield val.value;
        }
    }
}
/**
 * Create a new iterable containing only the elements from the input iterable for which the filter
 * function returns `true`.
 *
 * If you want to narrow the type of your returned array, you can use `filter` instead.
 *
 * @param filterFunc A function that takes an element and its index, and returns a boolean. If the
 * result is `true`, the element is yielded.
 * @param arr The input iterable.
 * @yields The elements from the input iterable that satisfy the filter function.
 */
export function* filterBool(filterFunc, arr) {
    let i = 0;
    for (const el of arr) {
        if (filterFunc(el, i++)) {
            yield el;
        }
    }
}
/**
 * Create a new iterable that contains only the unique elements from the input iterable.
 *
 * NOTE: This function is like Pythons `itertools.unique`, in that it REQUIRES the input to already
 * be sorted, otherwise IT WILL NOT remove all duplicates.
 *
 * @param arr The input iterable.
 * @param isEqual A function that determines if two elements are equal.
 * @yields A new sorted iterable, with unique elements in ascending order.
 */
export function* uniqueSorted(arr, isEqual) {
    const maybeFirst = first(arr);
    if (!maybeFirst.isJust()) {
        return;
    }
    let last = maybeFirst.extract();
    yield last;
    for (const item of arr) {
        if (!isEqual(item, last)) {
            yield item;
        }
        last = item;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function* unique(getKey, arr) {
    const seen = new Set();
    for (const item of arr) {
        const key = getKey(item);
        if (!seen.has(key)) {
            seen.add(key);
            yield item;
        }
    }
}
/**
 * Batch an iterable into chunks of a specific size, both in amount of items per batch and an
 * arbitrary "size" of a batch.
 *
 * @param input The iterable that should be batched.
 * @param props The way to do the batching, see {@link BatchProps} for more information.
 * @yields Not empty arrays that contain items from `input` (in the same order as `input`) but is
 * chunked into batches.
 */
export function* sizedBatch(input, props) {
    let cur = [];
    let curSize = 0;
    for (const el of input) {
        const newSize = props.getSize(el);
        if (NonEmptyList.isNonEmpty(cur) &&
            (cur.length === props.maxBatchAmount || newSize + curSize > props.maxBatchSize)) {
            yield cur;
            curSize = 0;
            cur = [];
        }
        cur.push(el);
        curSize += newSize;
    }
    if (NonEmptyList.isNonEmpty(cur)) {
        yield cur;
    }
}
/**
 * Batch an iterable into batches of size `size`.
 *
 * This is an optimized version of {@link sizedBatch}, which only works for batches of a single
 * size.
 *
 * @param input The iterable that should be batched.
 * @param size The maximum size a batch should have, the last batch might have a smaller size.
 * @yields Not empty arrays that contain at most `size` amount of elements.
 */
export function* batch(input, size) {
    AssertionError.assert(size > 0, () => `The size should larger than 0, it was ${size}`);
    let cur = [];
    for (const el of input) {
        if (NonEmptyList.isNonEmpty(cur) && cur.length === size) {
            yield cur;
            cur = [];
        }
        cur.push(el);
    }
    if (NonEmptyList.isNonEmpty(cur)) {
        yield cur;
    }
}
/**
 * Join an iterable together in a readable way.
 *
 * @param arr The iterable to join together.
 * @param sep The separator to use for the last item.
 * @returns A string that is the readable join of `arr`.
 */
export function readableJoin(arr, sep = 'and') {
    const start = [];
    let end;
    for (const el of arr) {
        if (end != null) {
            start.push(end);
        }
        end = el;
    }
    if (end == null) {
        return '';
    }
    else if (start.length === 0) {
        return end;
    }
    return `${start.join(', ')}, ${sep} ${end}`;
}
/**
 * Make an iterator that skips elements from the iterable while the predicate is true and afterwards
 * yields every element.
 *
 * This function is modeled after Python's `itertools.dropwhile`.
 *
 * @param iter The iterable to process.
 * @param predicate The predicate to check.
 * @yields The first element for which `predicate` returns `false`, and all elements after it.
 */
export function* dropWhile(iter, predicate) {
    let index = 0;
    let shouldDrop = true;
    for (const element of iter) {
        if (shouldDrop && !predicate(element, index)) {
            shouldDrop = false;
        }
        if (!shouldDrop) {
            yield element;
        }
        index++;
    }
}
/**
 * A utility function that lazily evaluates an input generator function only when the returned
 * iterable is consumed.
 *
 * @param getInp A function that returns an iterable of type `T`.
 * @returns An iterable that defers the execution of the input function until it is consumed.
 *
 * @example
 * const lazy = lazyIterable(() => {
 *     console.log("Generator executed!");
 *     return [1, 2, 3];
 * });
 *
 * // No output yet because the iterable is not consumed
 * console.log("Before consumption");
 *
 * for (const item of lazy) {
 *     console.log(item);
 * }
 * // Logs:
 * // "Generator executed!"
 * // "1"
 * // "2"
 * // "3"
 */
export function lazyIterable(getInp) {
    return {
        /** @returns The new iterable. */
        [Symbol.iterator]() {
            return getInp()[Symbol.iterator]();
        },
    };
}
/** @inheritdoc */
export function* zip(...lists) {
    if (lists.length === 0) {
        return;
    }
    const iters = lists.map(list => list[Symbol.iterator]());
    for (;;) {
        const cur = [];
        for (let j = 0; j < iters.length; j++) {
            const curIter = iters[j];
            const next = curIter.next();
            if (next.done ?? false) {
                return;
            }
            cur.push(next.value);
        }
        yield cur;
    }
}
/**
 * Iterate over an array in reverse order, starting with the last item.
 *
 * @param xs The array to iterate over.
 * @yields The elements of `xs` in reverse order.
 * @returns An iterable over `xs` in reverse order.
 */
export function* backward(xs) {
    for (let i = xs.length - 1; i >= 0; i--) {
        yield xs[i];
    }
}
/**
 * Counts the number of elements in an iterable (like an array) that
 * satisfy a given predicate function.
 *
 * @param iterable The array or iterable to count elements from.
 * @param predicate A function that returns true for elements that should be counted.
 * @returns The total number of elements that satisfy the predicate.
 */
export function count(iterable, predicate) {
    let result = 0;
    for (const item of iterable) {
        if (predicate(item)) {
            result++;
        }
    }
    return result;
}
